import React, { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Container, ThemeProvider } from '@mui/material';
import WorkerBottomTabs from '../components/worker/WorkerBottomTabs';
import WorkerAppBar from '../components/worker/WorkerAppBar';
import WorkerPageTransition from '../components/worker/WorkerPageTransition';
import WorkerAppDownloadBanner from '../components/worker/WorkerAppDownloadBanner';
import { useAuth } from '../contexts/AuthContext';
import { WorkerToastProvider } from '../contexts/WorkerToastContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useWorkerPreferredLanguage } from '../hooks/useWorkerPreferredLanguage';
import { setLanguage } from '../i18n';
import { getWorkerTheme } from '../theme/workerTheme';

/**
 * Layout for worker routes: top bar, side WorkerNav, main content.
 * Uses worker-only theme (design system) for modern, mobile-friendly look.
 * When used from ConditionalJobsBoardLayout (e.g. /apply/...), children is <Outlet /> so the child route renders.
 * When used from /c1 routes, no children so internal <Outlet /> renders the /c1 child.
 */
const C1WorkerLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const preferredLanguage = useWorkerPreferredLanguage();
  const workerTheme = useMemo(() => getWorkerTheme(), []);
  usePushNotifications(user?.uid);

  useEffect(() => {
    setLanguage(preferredLanguage);
  }, [preferredLanguage]);

  return (
    <ThemeProvider theme={workerTheme}>
      <WorkerToastProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
          <WorkerAppBar />
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <Container
              component="main"
              sx={{
                flex: 1,
                // Flex children default to min-width:auto, which refuses to
                // shrink below the content's intrinsic width — on mobile that
                // pushes cards wider than the viewport and their overflow:hidden
                // clips text/buttons. minWidth:0 lets it shrink to fit.
                minWidth: 0,
                py: 3,
                px: { xs: 2, sm: 3 },
                // Clear the fixed bottom tab bar (56px + iOS safe area).
                pb: 'calc(80px + env(safe-area-inset-bottom))',
                overflow: 'auto',
                maxWidth: { sm: 720 },
                borderRadius: 0,
                /** Keep main (incl. page transition transforms) below WorkerNav drawer stacking (z-index). */
                position: 'relative',
                zIndex: 0,
                isolation: 'isolate',
              }}
            >
              <WorkerAppDownloadBanner />
              <WorkerPageTransition>{children ?? <Outlet />}</WorkerPageTransition>
            </Container>
          </Box>
          <WorkerBottomTabs />
        </Box>
      </WorkerToastProvider>
    </ThemeProvider>
  );
};

export default C1WorkerLayout;
